import {
  useGetApInvoiceTransactionGeneralInputOptions,
  useGetTransactionItemPulldownOption,
} from '@/context/services/transaction_items/Detail/TransactionItemDetail.service';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

type UsePulldownOptionsParams = {
  propertyId: string;
  per?: number;
};

export const usePulldownOptions = ({
  propertyId,
  per = 25,
}: UsePulldownOptionsParams) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [query, setQuery] = useState<string>('');

  const getApInvoiceTransactionGeneralInputOptions =
    useGetApInvoiceTransactionGeneralInputOptions();

  const {
    data: apiData,
    error,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: [
      'ap_invoice_transaction_general_input_options',
      propertyId,
      query,
    ],
    queryFn: async ({ pageParam }) => {
      const response = await getApInvoiceTransactionGeneralInputOptions(
        propertyId,
        {
          keyword: query,
          page: pageParam,
          per: per,
        }
      );
      return response.data?.ap_general_input_options ?? [];
    },
    getNextPageParam: (lastPage, _, lastPageParam) => {
      if (lastPage && lastPage.length === per) {
        return lastPageParam + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: menuIsOpen,
  });

  const fetchMore = useCallback(
    async (callback: () => void) => {
      if (hasNextPage) {
        await fetchNextPage();
      }
      callback();
    },
    [fetchNextPage, hasNextPage]
  );

  const onSearch = useCallback((value: string) => {
    startTransition(() => {
      setQuery(value);
    });
  }, []);

  const handleDropdownVisibleChange = useCallback((open: boolean) => {
    if (open) {
      setMenuIsOpen(true);
    } else {
      setMenuIsOpen(false);
    }
  }, []);

  return {
    isLoading,
    error,
    onSearch,
    fetchMore,
    handleDropdownVisibleChange,
    optionList: apiData?.pages.flat() ?? [],
    query,
  };
};

export const usePulldownOption = ({
  id,
  propertyId,
}: UsePulldownOptionsParams & { id: string | undefined }) => {
  const [initialLoad, setInitialLoad] = useState(true);
  const getTransactionItemPulldownOption =
    useGetTransactionItemPulldownOption();

  const {
    data: optionData,
    error,
    isLoading,
  } = useQuery({
    queryKey: ['ap_invoice_transaction_general_input_options', propertyId, id],
    queryFn: async () => {
      const optionData = await getTransactionItemPulldownOption(
        propertyId,
        id ?? ''
      );
      return optionData.data;
    },
    enabled: id !== undefined && initialLoad,
  });

  const defaultOption = useMemo(() => {
    if (!optionData?.id) {
      return [];
    }

    return [
      {
        value: optionData?.id?.toString() ?? '',
        label: (optionData?.label ?? '').concat(
          optionData?.code ? `（${optionData?.code}）` : ''
        ),
        caption: optionData?.code ?? '',
      },
    ];
  }, [optionData]);

  useEffect(() => {
    if (initialLoad && optionData) {
      setInitialLoad(false);
    }
  }, [initialLoad, optionData]);

  return {
    defaultOption,
    isLoading,
    error,
  };
};
